import "../../global.css";
import "./color_sizes.css";
import "../../components/DragDropFiles.css";
import { useEffect, useRef, useState } from "react";
import { userRequest, publicRequestSt } from "../../requestMethods";
import Swal from "sweetalert2";
import { useCompany } from "../../components/context/CompanyContext";
import CustomGrid from "../../components/grid/CustomGrid";
import RowInput from "../../components/grid/RowInput";
import { useSelector } from "react-redux";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import SaveIcon from "@mui/icons-material/Save";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  buildSizeDeleteMessage,
  buildSizeDeletedMessage,
} from "../../util/sizeDelete";
import { buildColorUpdate } from "../../util/colorEdit";

export default function ColorsSizes() {
  const [colors, setColors] = useState([{}]);
  const [sizes, setSizes] = useState([{}]);
  const { companyId } = useCompany();
  const currentUser = useSelector((state) => state.user.currentUser);
  const [color, setColor] = useState("#ffffff");
  const [cor, setCor] = useState("");
  const [size, setSize] = useState("");
  const dragIndex = useRef(null);

  useEffect(() => {
    if (companyId > 0) {
      getColors();
      getSizes();
    }
  }, [companyId]);

  const getColors = async () => {
    const res = await userRequest.get(`${companyId}/products/colors`);
    setColors(
      res.data.map((item) => ({
        colorId: item.colorId,
        rgb: item.rgb,
        hexa: item.rgb,
        ref: item.ref,
        label: item.label,
      }))
    );
  };

  const getSizes = async () => {
    const res = await userRequest.get(`${companyId}/products/sizes`);

    setSizes(res.data);
  };

  const createSizes = async () => {
    if (size === "") {
      Swal.fire("Atenção!", "Informe o tamanho!", "warning");
      return;
    }
    const res = await userRequest.post(
      `${companyId}/products/sizes/findcreate`,
      {
        label: size,
        ref: size,
      }
    );
    if (Object.keys(res.data).length > 0) {
      getSizes();
      Swal.fire("Parabéns!", "Tamanho criado com sucesso!", "success");
    } else {
      Swal.fire("Erro!", "Erro ao gravar tamanho!", "warning");
    }
  };

  const deleteSize = async (sizeToDelete) => {
    let usage;

    try {
      const res = await userRequest.get(
        `${companyId}/products/sizes/${sizeToDelete.id}/usage`
      );
      usage = res.data;
    } catch (error) {
      console.error(error);
      Swal.fire("Erro!", "Erro ao consultar o uso do tamanho!", "error");
      return;
    }

    const { title, text } = buildSizeDeleteMessage({
      ...usage,
      label: sizeToDelete.label,
    });

    const result = await Swal.fire({
      title,
      text,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#EE8253",
      confirmButtonText: "Sim, excluir!",
      cancelButtonColor: "#d33",
      cancelButtonText: "Cancelar",
    });

    if (!result.isConfirmed) return;

    try {
      const res = await userRequest.delete(
        `${companyId}/products/sizes/${sizeToDelete.id}`
      );
      await getSizes();
      Swal.fire("Pronto!", buildSizeDeletedMessage(res.data), "success");
    } catch (error) {
      console.error(error);
      Swal.fire("Erro!", "Erro ao excluir o tamanho!", "error");
    }
  };

  const createColors = async () => {
    if (cor === "" || cor.length < 3) {
      Swal.fire("Atenção!", "Informe a cor e o hexadecimal!", "warning");
      return;
    }

    const res = await userRequest.post(
      `${companyId}/products/colors/findcreate`,
      {
        label: cor,
        rgb: color,
        ref: cor,
      }
    );

    if (Object.keys(res.data).length > 0) {
      getColors();
      Swal.fire("Parabéns!", "Cor criada com sucesso!", "success");
    } else {
      Swal.fire("Erro!", "Erro ao gravar a cor!", "warning");
    }
  };
  const handleDragStart = (index) => {
    dragIndex.current = index;
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (dragIndex.current === null || dragIndex.current === index) return;
    const reordered = [...sizes];
    const [moved] = reordered.splice(dragIndex.current, 1);
    reordered.splice(index, 0, moved);
    dragIndex.current = index;
    setSizes(reordered);
  };

  const handleDragEnd = () => {
    dragIndex.current = null;
  };

  const saveSizesOrder = async () => {
    await userRequest.put(`${companyId}/products/sizes/order`, {
      sizeIds: sizes.map((s) => s.id),
    });
    Swal.fire("Parabéns!", "Ordem salva com sucesso!", "success");
  };

  const updateColor = async (colorId, field, newValue) => {
    if (!currentUser.isAdmin) {
      Swal.fire("Atenção!", "Você não tem permissão para editar a cor!", "warning");
      getColors();
      return;
    }
    const { ok, msg, payload } = buildColorUpdate(field, newValue);
    if (!ok) {
      Swal.fire("Atenção!", msg, "warning");
      getColors();
      return;
    }
    const res = await userRequest.put(`${companyId}/products/colors/${colorId}`, payload);
    if (Object.keys(res.data).length > 0) {
      getColors();
      Swal.fire("Parabéns!", "Cor alterada com sucesso!", "success");
    } else {
      Swal.fire("Erro!", "Erro ao atualizar cor!", "warning");
    }
  };

  const columns = [
    {
      field: "label",
      headerName: "Descrição",
      width: 250,
      align: "left",
      headerAlign: "left",
      renderCell: ({ row }) => (
        <RowInput
          value={row.label}
          rowId={row.colorId}
          onBlur={(id, newValue) => updateColor(id, "label", newValue)}
          width="220px"
          placeholder="Descrição"
        />
      ),
    },
    {
      field: "rgb",
      headerName: "Paleta",
      align: "center",
      headerAlign: "center",
      width: 200,
      renderCell: ({ row }) => (
        <div style={{ height: "28px", width: "180px", backgroundColor: row.rgb, borderRadius: "8px" }} />
      ),
    },
    {
      field: "hexa",
      headerName: "Hexadecimal",
      align: "center",
      headerAlign: "center",
      width: 200,
      renderCell: ({ row }) => (
        <RowInput
          value={row.hexa}
          rowId={row.colorId}
          onBlur={(id, newValue) => updateColor(id, "rgb", newValue)}
          width="150px"
          placeholder="#000000"
        />
      ),
    },
  ];
  return (
    <div className="cust-page">
      <div className="cust-title">Cores e Tamanhos</div>

      {/* Tamanhos */}
      <div className="cust-card">
        <div className="cust-section-header">Tamanhos</div>
        <div className="cust-fields">
          <div className="cust-field">
            <label className="cust-label">Tamanho</label>
            <input
              className="cust-input"
              type="text"
              name="tamanho"
              onChange={(e) => setSize(e.target.value)}
            />
          </div>
        </div>
        <div style={{ padding: "0 16px 16px" }}>
          <Button
            variant="contained"
            color="secondary"
            size="small"
            startIcon={<SaveIcon />}
            onClick={createSizes}
          >
            Adicionar Tamanho
          </Button>
        </div>
        <div style={{ padding: "0 16px 16px" }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {sizes.map((size, index) => (
              <div
                key={size.id}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  padding: "6px 12px",
                  border: "1px solid #ccc",
                  borderRadius: 6,
                  background: "#fff",
                  cursor: "grab",
                  userSelect: "none",
                }}
              >
                <DragIndicatorIcon style={{ fontSize: 18, color: "#999" }} />
                <span style={{ fontSize: 10, color: "#aaa", minWidth: 14 }}>{index + 1}</span>
                <span style={{ fontWeight: 500 }}>{size.label}</span>
                {size.id && (
                  <IconButton
                    size="small"
                    title={`Excluir o tamanho ${size.label}`}
                    aria-label={`Excluir o tamanho ${size.label}`}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={() => deleteSize(size)}
                    style={{ padding: 2, marginLeft: 2 }}
                  >
                    <DeleteOutlineIcon style={{ fontSize: 16, color: "#d33" }} />
                  </IconButton>
                )}
              </div>
            ))}
          </div>
          <div style={{ marginTop: 12 }}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<SaveIcon />}
              onClick={saveSizesOrder}
            >
              Salvar Ordem
            </Button>
          </div>
        </div>
      </div>

      {/* Cores */}
      <div className="cust-card">
        <div className="cust-section-header">Cores</div>
        <div className="cust-fields">
          <div className="cust-field">
            <label className="cust-label">Nome da Cor</label>
            <input
              className="cust-input"
              type="text"
              name="cor"
              onChange={(e) => setCor(e.target.value)}
            />
          </div>
          <div className="cust-field">
            <label className="cust-label">Hexadecimal</label>
            <input
              type="color"
              name="rgb"
              defaultValue="#ff0000"
              style={{ width: "60px", height: "36px", border: "none", padding: "0px", cursor: "pointer" }}
              onChange={(e) => setColor(e.target.value)}
            />
          </div>
        </div>
        <div style={{ padding: "0 16px 16px" }}>
          <Button
            variant="contained"
            color="secondary"
            size="small"
            startIcon={<SaveIcon />}
            onClick={createColors}
          >
            Adicionar Cor
          </Button>
        </div>
        {colors && colors.length > 1 && (
          <div style={{ padding: "16px" }}>
            <CustomGrid
              gridId="colorSizes"
              getRowId={(row) => row.colorId}
              rows={colors}
              columns={columns}
              tableHeight="500px"
            />
          </div>
        )}
      </div>
    </div>
  );
}
